import { useState, useCallback, useMemo, useEffect } from 'react';
import { Database, Plus, RefreshCw, AlertTriangle, List, LayoutGrid, ClipboardList } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { EmptyState, ErrorBoundary, useToast } from '@/components/common';
import { LoadingSpinner } from '@/components/common';
import { useAuthStore } from '@/stores/useAuthStore';
import { useFirmContextStore } from '@/stores/useFirmContextStore';
import {
  useKbEntries,
  useKbStats,
  useResolvedStory,
  useKbCategories,
  useCreateKbEntry,
  useUpdateKbEntry,
  useDeleteKbEntry,
  useMarkStale,
  useClearStale,
  useRefreshEmbeddings,
  useCheckDuplicates,
  type KbEntry,
  type KbEntryFilters,
  type CreateEntryRequest,
  type UpdateEntryRequest,
  type KbSourceType,
} from '@/hooks/useKnowledgeBase';
import {
  KbStatsCard,
  KbFilterBar,
  KbEntryTable,
  KbEntryCard,
  KbEntryForm,
  KbEntryDetail,
  ResolvedStoryView,
  OnboardingWizard,
  OnboardingProgress,
  type KbSortOption,
  type StaleFilter,
  type SortField,
  type SortDirection,
} from '@/features/admin/kb';
import { useOnboardingProgress } from '@/hooks/useOnboarding';

type ViewMode = 'table' | 'cards';
type ModalMode = 'create' | 'edit' | 'view' | null;
type PageTab = 'entries' | 'resolved' | 'onboarding';

/**
 * Knowledge Base Admin Page wrapped with error boundary.
 */
export function KnowledgeBasePage() {
  const handleError = useCallback((error: Error) => {
    console.error('[KnowledgeBasePage] Error caught by boundary:', error);
  }, []);

  return (
    <ErrorBoundary onError={handleError}>
      <KnowledgeBasePageContent />
    </ErrorBoundary>
  );
}

/**
 * Internal Knowledge Base page content.
 */
function KnowledgeBasePageContent() {
  const { success: showSuccess, error: showError, info: showInfo } = useToast();

  // Tab state
  const [activeTab, setActiveTab] = useState<PageTab>('resolved');
  const [showOnboardingWizard, setShowOnboardingWizard] = useState(false);

  // Auth context — derive firm_id from JWT
  const { user } = useAuthStore();
  const activeFirmId = useFirmContextStore((s) => s.activeFirmId);
  const effectiveFirmId = user?.isSuperAdmin ? activeFirmId : (user?.firmId ?? 0);
  const concreteFirmId =
    typeof effectiveFirmId === 'number' && effectiveFirmId > 0 ? effectiveFirmId : undefined;

  // Regular users: use JWT firm_id. SuperAdmins: use header firm context.
  const onboardingFirmId = effectiveFirmId ?? 0;
  const shouldPromptForOnboardingFirm = user?.isSuperAdmin && onboardingFirmId === 0;
  const { data: onboardingProgress, isLoading: isLoadingOnboardingProgress } =
    useOnboardingProgress(onboardingFirmId, {
      enabled: activeTab === 'onboarding' && !shouldPromptForOnboardingFirm,
    });

  // View state
  const [viewMode, setViewMode] = useState<ViewMode>('table');
  const [modalMode, setModalMode] = useState<ModalMode>(null);
  const [selectedEntry, setSelectedEntry] = useState<KbEntry | null>(null);
  const [expandedEntryId, setExpandedEntryId] = useState<number | null>(null);

  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedSourceType, setSelectedSourceType] = useState<KbSourceType | null>(null);
  const [staleFilter, setStaleFilter] = useState<StaleFilter>('all');
  const [sortOption, setSortOption] = useState<KbSortOption>('newest');
  const [page, setPage] = useState(0);
  const pageSize = 20;

  // Table sort state
  const [sortField, setSortField] = useState<SortField | undefined>(undefined);
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // Selection state for bulk actions
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  // Duplicate check state
  const [checkQuestion, setCheckQuestion] = useState('');

  // Build API filters
  const filters: KbEntryFilters = useMemo(() => {
    return {
      search: searchQuery || undefined,
      category: selectedCategory || undefined,
      source_type: selectedSourceType || undefined,
      is_stale: staleFilter === 'all' ? undefined : staleFilter === 'stale',
      firm_id: concreteFirmId,
      offset: page * pageSize,
      limit: pageSize,
    };
  }, [searchQuery, selectedCategory, selectedSourceType, staleFilter, page, concreteFirmId]);

  // API hooks
  const { data: entriesData, isLoading: isLoadingEntries, error: entriesError } = useKbEntries(filters);
  const { data: stats, isLoading: isLoadingStats } = useKbStats(effectiveFirmId);
  const { data: categories = [] } = useKbCategories();
  const { data: duplicateResult, isLoading: isCheckingDuplicates } = useCheckDuplicates(checkQuestion, {
    enabled: !!checkQuestion && checkQuestion.length > 10,
  });
  const { data: resolvedStory, isLoading: isLoadingResolvedStory } = useResolvedStory({
    enabled: activeTab === 'resolved',
  });

  // Mutations
  const createEntry = useCreateKbEntry();
  const updateEntry = useUpdateKbEntry();
  const deleteEntry = useDeleteKbEntry();
  const markStale = useMarkStale();
  const clearStale = useClearStale();
  const refreshEmbeddings = useRefreshEmbeddings();

  // Sort entries locally for table view
  const entriesList = entriesData?.entries;
  const sortedEntries = useMemo(() => {
    if (!entriesList) return [];

    const entries = [...entriesList];

    // Apply client-side sort based on sortOption
    switch (sortOption) {
      case 'newest':
        entries.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
        break;
      case 'oldest':
        entries.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
        break;
      case 'question-az':
        entries.sort((a, b) => a.question_text.localeCompare(b.question_text));
        break;
      case 'question-za':
        entries.sort((a, b) => b.question_text.localeCompare(a.question_text));
        break;
    }

    // Apply table sort if set
    if (sortField) {
      entries.sort((a, b) => {
        let aVal: string | number = '';
        let bVal: string | number = '';

        switch (sortField) {
          case 'question_text':
            aVal = a.question_text;
            bVal = b.question_text;
            break;
          case 'category':
            aVal = a.category || '';
            bVal = b.category || '';
            break;
          case 'source_type':
            aVal = a.source_type;
            bVal = b.source_type;
            break;
          case 'created_at':
            aVal = new Date(a.created_at).getTime();
            bVal = new Date(b.created_at).getTime();
            break;
          case 'updated_at':
            aVal = new Date(a.updated_at).getTime();
            bVal = new Date(b.updated_at).getTime();
            break;
        }

        if (typeof aVal === 'string' && typeof bVal === 'string') {
          return sortDirection === 'asc'
            ? aVal.localeCompare(bVal)
            : bVal.localeCompare(aVal);
        }
        return sortDirection === 'asc' ? (aVal as number) - (bVal as number) : (bVal as number) - (aVal as number);
      });
    }

    return entries;
  }, [entriesList, sortOption, sortField, sortDirection]);

  // Handlers
  const handleSort = useCallback((field: SortField) => {
    setSortField((prev) => {
      if (prev === field) {
        setSortDirection((dir) => (dir === 'asc' ? 'desc' : 'asc'));
        return field;
      }
      setSortDirection('asc');
      return field;
    });
  }, []);

  const handleCreateEntry = useCallback(() => {
    setSelectedEntry(null);
    setCheckQuestion('');
    setModalMode('create');
  }, []);

  const handleEditEntry = useCallback((entry: KbEntry) => {
    setSelectedEntry(entry);
    setModalMode('edit');
  }, []);

  const handleViewEntry = useCallback((entry: KbEntry) => {
    setSelectedEntry(entry);
    setModalMode('view');
  }, []);

  const handleCloseModal = useCallback(() => {
    setModalMode(null);
    setSelectedEntry(null);
    setCheckQuestion('');
  }, []);

  // Close the create/edit/view modal on Escape.
  useEffect(() => {
    if (!modalMode) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleCloseModal();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [modalMode, handleCloseModal]);

  const handleSubmitEntry = useCallback(
    async (data: CreateEntryRequest | UpdateEntryRequest) => {
      try {
        if (modalMode === 'create') {
          await createEntry.mutateAsync(data as CreateEntryRequest);
          showSuccess('Entry Created', 'The knowledge base entry has been created.');
        } else if (modalMode === 'edit' && selectedEntry) {
          await updateEntry.mutateAsync({
            entryId: selectedEntry.id,
            request: data as UpdateEntryRequest,
          });
          showSuccess('Entry Updated', 'The knowledge base entry has been updated.');
        }
        handleCloseModal();
      } catch {
        showError('Error', 'Failed to save the entry. Please try again.');
      }
    },
    [modalMode, selectedEntry, createEntry, updateEntry, showSuccess, showError, handleCloseModal]
  );

  const handleDeleteEntry = useCallback(
    async (entry: KbEntry) => {
      if (!window.confirm(`Are you sure you want to delete this entry?\n\n"${entry.question_text.substring(0, 100)}..."`)) {
        return;
      }

      try {
        await deleteEntry.mutateAsync(entry.id);
        showSuccess('Entry Deleted', 'The knowledge base entry has been deleted.');
        setSelectedIds((prev) => {
          const next = new Set(prev);
          next.delete(entry.id);
          return next;
        });
      } catch {
        showError('Error', 'Failed to delete the entry. Please try again.');
      }
    },
    [deleteEntry, showSuccess, showError]
  );

  const handleMarkStale = useCallback(
    async (entry: KbEntry) => {
      const input = window.prompt('Enter a reason for marking this entry as stale (min 5 chars):')?.trim();
      const reason = input && input.length >= 5 ? input : 'Manual review requested';
      try {
        await markStale.mutateAsync({ entryId: entry.id, reason });
        showInfo('Marked as Stale', 'The entry has been marked as stale.');
      } catch {
        showError('Error', 'Failed to mark entry as stale.');
      }
    },
    [markStale, showInfo, showError]
  );

  const handleClearStale = useCallback(
    async (entry: KbEntry) => {
      try {
        await clearStale.mutateAsync(entry.id);
        showSuccess('Stale Cleared', 'The entry has been marked as current.');
      } catch {
        showError('Error', 'Failed to clear stale status.');
      }
    },
    [clearStale, showSuccess, showError]
  );

  const handleRefreshEmbeddings = useCallback(async () => {
    const entryIds = selectedIds.size > 0 ? Array.from(selectedIds) : undefined;
    const message = entryIds
      ? `Refresh embeddings for ${entryIds.length} selected entries?`
      : 'Refresh embeddings for all entries? This may take a while.';

    if (!window.confirm(message)) {
      return;
    }

    try {
      const result = await refreshEmbeddings.mutateAsync(entryIds);
      showSuccess('Embeddings Refreshed', `Updated ${result.refreshed} entries.`);
      setSelectedIds(new Set());
    } catch {
      showError('Error', 'Failed to refresh embeddings.');
    }
  }, [selectedIds, refreshEmbeddings, showSuccess, showError]);

  const handleBulkMarkStale = useCallback(async () => {
    if (selectedIds.size === 0) return;

    const input = window.prompt(`Mark ${selectedIds.size} entries as stale. Enter reason (min 5 chars):`)?.trim();
    const reason = input && input.length >= 5 ? input : 'Manual review requested';
    try {
      await Promise.all(
        Array.from(selectedIds).map((id) => markStale.mutateAsync({ entryId: id, reason }))
      );
      showInfo('Marked as Stale', `${selectedIds.size} entries marked as stale.`);
      setSelectedIds(new Set());
    } catch {
      showError('Error', 'Failed to mark some entries as stale.');
    }
  }, [selectedIds, markStale, showInfo, showError]);

  const handleToggleSelect = useCallback((entryId: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(entryId)) {
        next.delete(entryId);
      } else {
        next.add(entryId);
      }
      return next;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    if (sortedEntries.every((e) => selectedIds.has(e.id))) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(sortedEntries.map((e) => e.id)));
    }
  }, [sortedEntries, selectedIds]);

  const handleToggleExpand = useCallback((entryId: number) => {
    setExpandedEntryId((prev) => (prev === entryId ? null : entryId));
  }, []);

  // Pagination
  const totalPages = entriesData ? Math.ceil(entriesData.total / pageSize) : 0;
  const hasNextPage = page < totalPages - 1;
  const hasPrevPage = page > 0;

  // Show loading state
  if (isLoadingEntries && !entriesData) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  // Show error state
  if (entriesError) {
    // Check for 403 Forbidden — user lacks admin permission
    const is403 = (entriesError as { response?: { status?: number } })?.response?.status === 403;

    return (
      <Card>
        <CardContent className="pt-6">
          <EmptyState
            icon={AlertTriangle}
            title={is403 ? 'Permission Denied' : 'Failed to Load Knowledge Base'}
            description={
              is403
                ? "You don't have permission to access the Knowledge Base. Contact your administrator to request the insight:admin role."
                : 'There was an error loading the knowledge base entries.'
            }
          >
            {!is403 && (
              <Button onClick={() => window.location.reload()}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Retry
              </Button>
            )}
          </EmptyState>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Knowledge Base</h1>
          <p className="text-muted-foreground">
            Manage approved response entries used for DDQ/RFP prepopulation
          </p>
        </div>
        {activeTab === 'entries' && (
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={handleRefreshEmbeddings} disabled={refreshEmbeddings.isPending}>
              <RefreshCw className={`h-4 w-4 mr-2 ${refreshEmbeddings.isPending ? 'animate-spin' : ''}`} />
              Refresh Embeddings
              {selectedIds.size > 0 && ` (${selectedIds.size})`}
            </Button>
            <Button onClick={handleCreateEntry}>
              <Plus className="h-4 w-4 mr-2" />
              Add Entry
            </Button>
          </div>
        )}
      </div>

      {/* Tab Navigation */}
      <div className="flex items-center gap-1 border-b">
        <button
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'resolved'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
          onClick={() => { setActiveTab('resolved'); setShowOnboardingWizard(false); }}
        >
          <Database className="h-4 w-4 inline mr-2" />
          Entries
        </button>
        <button
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'onboarding'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
          onClick={() => setActiveTab('onboarding')}
        >
          <ClipboardList className="h-4 w-4 inline mr-2" />
          Onboarding
          {onboardingProgress && onboardingProgress.percent < 100 && (
            <span className="ml-2 text-xs bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded-full">
              {onboardingProgress.percent}%
            </span>
          )}
        </button>
      </div>

      {activeTab === 'onboarding' && (
        shouldPromptForOnboardingFirm ? (
          <p className="text-sm text-muted-foreground">
            Select a firm in the header to view onboarding
          </p>
        ) : showOnboardingWizard ? (
          <OnboardingWizard
            firmId={onboardingFirmId}
            onBack={() => setShowOnboardingWizard(false)}
          />
        ) : (
          <div className="max-w-2xl mx-auto">
            <OnboardingProgress
              progress={onboardingProgress}
              isLoading={isLoadingOnboardingProgress}
              onStart={() => setShowOnboardingWizard(true)}
            />
          </div>
        )
      )}

      {/* Resolved Story Tab - Wellington #5 inheritance-provenance (read-only) */}
      {activeTab === 'resolved' && (
        <ResolvedStoryView
          story={resolvedStory}
          isLoading={isLoadingResolvedStory}
        />
      )}

      {/* Entries Tab - Main Content */}
      {activeTab === 'entries' && (
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Stats Sidebar */}
        <div className="lg:col-span-1">
          <KbStatsCard stats={stats} isLoading={isLoadingStats} />
        </div>

        {/* Entries Section */}
        <div className="lg:col-span-3 space-y-4">
          {/* Filter Bar */}
          <KbFilterBar
            searchQuery={searchQuery}
            onSearchChange={(q) => {
              setSearchQuery(q);
              setPage(0);
            }}
            selectedCategory={selectedCategory}
            onCategoryChange={(c) => {
              setSelectedCategory(c);
              setPage(0);
            }}
            categories={categories}
            selectedSourceType={selectedSourceType}
            onSourceTypeChange={(s) => {
              setSelectedSourceType(s);
              setPage(0);
            }}
            staleFilter={staleFilter}
            onStaleFilterChange={(f) => {
              setStaleFilter(f);
              setPage(0);
            }}
            sortBy={sortOption}
            onSortChange={setSortOption}
            resultCount={entriesData?.total || 0}
          />

          {/* View Mode Toggle & Bulk Actions */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Button
                variant={viewMode === 'table' ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('table')}
              >
                <List className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === 'cards' ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('cards')}
              >
                <LayoutGrid className="h-4 w-4" />
              </Button>
            </div>

            {selectedIds.size > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">
                  {selectedIds.size} selected
                </span>
                <Button variant="outline" size="sm" onClick={handleBulkMarkStale}>
                  <AlertTriangle className="h-4 w-4 mr-1" />
                  Mark Stale
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setSelectedIds(new Set())}>
                  Clear
                </Button>
              </div>
            )}
          </div>

          {/* Entries Display */}
          {sortedEntries.length === 0 ? (
            <Card>
              <CardContent className="pt-6">
                <EmptyState
                  icon={Database}
                  title="No Entries Found"
                  description={
                    searchQuery || selectedCategory || selectedSourceType || staleFilter !== 'all'
                      ? 'Try adjusting your filters.'
                      : 'Add your first knowledge base entry to get started.'
                  }
                >
                  {!(searchQuery || selectedCategory || selectedSourceType || staleFilter !== 'all') && (
                    <Button onClick={handleCreateEntry}>
                      <Plus className="h-4 w-4 mr-2" />
                      Add Entry
                    </Button>
                  )}
                </EmptyState>
              </CardContent>
            </Card>
          ) : viewMode === 'table' ? (
            <Card>
              <KbEntryTable
                entries={sortedEntries}
                onEdit={handleEditEntry}
                onDelete={handleDeleteEntry}
                onMarkStale={handleMarkStale}
                onClearStale={handleClearStale}
                onSelect={handleViewEntry}
                selectedIds={selectedIds}
                onToggleSelect={handleToggleSelect}
                onSelectAll={handleSelectAll}
                sortField={sortField}
                sortDirection={sortDirection}
                onSort={handleSort}
              />
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {sortedEntries.map((entry) => (
                <KbEntryCard
                  key={entry.id}
                  entry={entry}
                  onEdit={() => handleEditEntry(entry)}
                  onDelete={() => handleDeleteEntry(entry)}
                  onMarkStale={() => handleMarkStale(entry)}
                  onClearStale={() => handleClearStale(entry)}
                  isExpanded={expandedEntryId === entry.id}
                  onToggleExpand={() => handleToggleExpand(entry.id)}
                />
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-4">
              <p className="text-sm text-muted-foreground">
                Showing {page * pageSize + 1} - {Math.min((page + 1) * pageSize, entriesData?.total || 0)} of{' '}
                {entriesData?.total || 0} entries
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => p - 1)}
                  disabled={!hasPrevPage}
                >
                  Previous
                </Button>
                <span className="text-sm text-muted-foreground">
                  Page {page + 1} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={!hasNextPage}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      )}

      {/* Create/Edit Modal */}
      {modalMode && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50"
            onClick={handleCloseModal}
          />

          {/* Modal Content */}
          <div className="relative z-10 max-h-[90vh] overflow-y-auto">
            {modalMode === 'view' && selectedEntry ? (
              <KbEntryDetail
                entry={selectedEntry}
                onClose={handleCloseModal}
                onEdit={handleEditEntry}
              />
            ) : (
              <KbEntryForm
                entry={modalMode === 'edit' ? selectedEntry : null}
                categories={categories}
                onSubmit={handleSubmitEntry}
                onCancel={handleCloseModal}
                isSubmitting={createEntry.isPending || updateEntry.isPending}
                duplicateCheck={
                  modalMode === 'create'
                    ? {
                        isChecking: isCheckingDuplicates,
                        result: duplicateResult || null,
                      }
                    : undefined
                }
                onQuestionChange={modalMode === 'create' ? setCheckQuestion : undefined}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
